import {COMPANY_KEYS, useRequestReferralCodeMutation} from "@/api/companyApi";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import NotFound from "@/components/Common/NotFound";
import Loader from "@/components/Common/kpi/Loader";
import JoinApplyForm from "@/components/Common/referral/JoinApplyForm";
import JoinSuccessPanel from "@/components/Common/referral/JoinSuccessPanel";
import {JoinPageResponseSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {createFileRoute} from "@tanstack/react-router";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";

const EmbedJoinSearchSchema = z.object({
  header: z.boolean().optional(),
});

export const Route = createFileRoute("/embed/join/$slug")({
  component: EmbedCompanyJoin,
  validateSearch: search => EmbedJoinSearchSchema.parse(search),
});

const INITIAL_FORM_VALUES = {email: "", handle: "", social: "instagram" as const};

function EmbedCompanyJoin() {
  const {t} = useTranslation("referral");
  const {slug} = Route.useParams();
  const search = Route.useSearch();
  const contentRef = useRef<HTMLDivElement>(null);

  const {data, isLoading} = useGenericQuery<JoinPageResponseSerializer>({
    queryKey: COMPANY_KEYS.qCompanyJoin(slug),
    enabled: !!slug,
  });

  const {
    mutate: requestReferralCode,
    data: referralResponse,
    isPending,
    isSuccess,
    variables: submittedForm,
  } = useRequestReferralCodeMutation(slug);

  const brandName = data?.company?.company_name ?? "";
  const picture =
    data?.company?.picture320 ?? data?.company?.picture640 ?? data?.company?.picture ?? CompanyLogoPlaceholder;
  const referralCode = referralResponse?.referral_code;
  const submittedPlatform = submittedForm?.platform;

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const postHeight = () => window.parent.postMessage({type: "refluenced:height", height: element.scrollHeight}, "*");
    const observer = new ResizeObserver(postHeight);
    observer.observe(element);
    postHeight();

    return () => observer.disconnect();
  }, [isLoading, isSuccess]);

  const hasTrackedOpenRef = useRef(false);
  useEffect(() => {
    if (!data?.company || hasTrackedOpenRef.current) return;
    hasTrackedOpenRef.current = true;
    capturePostHogEvent(PostHogEvents.JoinPageOpened, {
      brandSlug: slug,
      embedded: true,
      prefilled: false,
    });
  }, [data, slug]);

  if (isLoading) {
    return (
      <div ref={contentRef} className="flex min-h-40 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!data?.company) {
    return (
      <div ref={contentRef}>
        <NotFound />
      </div>
    );
  }

  return (
    <div
      ref={contentRef}
      className="text-neutral-black my-auto flex max-w-182 flex-col items-center gap-4 self-center bg-transparent pb-1.5">
      {search.header && (
        <div className="flex flex-col items-center gap-2">
          <img
            className="border-neutral-grey-light-dark rounded-2.5xl size-16 border-2 bg-white object-contain"
            src={picture}
            alt={brandName ? t("brand_logo_alt", {brand: brandName}) : t("brand_logo_alt_fallback")}
          />
          <span className="text-neutral-grey-dark text-center text-sm font-semibold tracking-wide uppercase">
            {brandName}
          </span>
        </div>
      )}
      {isSuccess ? (
        <div className="flex w-full max-w-182 flex-col items-center gap-5">
          <h2 className="text-neutral-black m-0 text-center text-2xl leading-tight font-semibold">
            {t("success_start_earning_title")}
          </h2>
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="text-neutral-grey-dark text-sm leading-3">
              {t("referral_code_pre_loaded", {brand: brandName})}
            </span>
            <span className="text-neutral-grey-dark text-sm leading-3">{t("just_download_and_sign_up")}</span>
          </div>
          <JoinSuccessPanel
            onCopy={() =>
              capturePostHogEvent(PostHogEvents.JoinPageReferralCodeCopied, {
                brandSlug: slug,
                embedded: true,
                platform: submittedPlatform,
                prefilled: false,
              })
            }
            onDownload={() =>
              capturePostHogEvent(PostHogEvents.JoinPageDownloadAppClicked, {
                brandSlug: slug,
                embedded: true,
                platform: submittedPlatform,
                prefilled: false,
              })
            }
            referralCode={referralCode ?? ""}
          />
        </div>
      ) : (
        <>
          <h2 className="text-neutral-black m-0 max-w-150 text-center text-2xl leading-tight font-semibold">
            {t("join_creator_community_title", {brand: brandName})}
          </h2>
          <JoinApplyForm
            initialValues={INITIAL_FORM_VALUES}
            isPending={isPending}
            onSubmit={values =>
              requestReferralCode(values, {
                onSuccess: () =>
                  capturePostHogEvent(PostHogEvents.JoinPageEmailHandleSubmitted, {
                    brandSlug: slug,
                    embedded: true,
                    platform: values.platform,
                    prefilled: false,
                  }),
              })
            }
          />
        </>
      )}
    </div>
  );
}
